// ═══════════════════════════════════════════════════════════════
// Roovivo — Admin Panel (Full CRM + Notary Portal)
// ═══════════════════════════════════════════════════════════════
import { useState, useEffect } from 'react';
import {
  adminLogin, adminStats, getProperties, getReservations, getReservation,
  seedProperties, createProperty, updateProperty, deleteProperty,
  advanceStage, addNote, verifyDoc,
} from './api';

const T = {
  bg: '#07070f', surface: '#0e0e1b', card: '#131320', card2: '#181828',
  border: '#22223a', gold: '#C9A96E', goldGlow: '#C9A96E22',
  cyan: '#00D4AA', cyanGlow: '#00D4AA15',
  text: '#F0EDE8', muted: '#6a6a8a', dim: '#3a3a5a',
  red: '#E05555', green: '#4CAF50', purple: '#9b6dff',
  ff: "'Outfit', sans-serif", ffD: "'Cormorant Garamond', serif",
};
const fmt = n => '€' + (n || 0).toLocaleString('en-MT');
const dateStr = d => d ? new Date(d).toLocaleDateString('en-MT', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const STAGE_OPTIONS = [
  { value: 'reservation_paid', label: '💳 Reservation Paid' },
  { value: 'notary_assigned',  label: '⚖️ Notary Assigned' },
  { value: 'konvenju_drafted', label: '📋 Konvenju Drafted' },
  { value: 'poa_submitted',    label: '✍️ PoA Submitted' },
  { value: 'deposit_paid',     label: '💰 Deposit Paid' },
  { value: 'due_diligence',    label: '🔍 Due Diligence' },
  { value: 'deed_prepared',    label: '📜 Deed Prepared' },
  { value: 'completed',        label: '🎉 Completed' },
];

const stageColor = s => ({
  reservation_paid: T.cyan, notary_assigned: T.cyan, konvenju_drafted: T.gold,
  poa_submitted: T.gold, deposit_paid: T.gold, due_diligence: '#9b6dff',
  deed_prepared: '#9b6dff', completed: T.green, cancelled: T.red,
}[s] || T.muted);

// ── SHARED ───────────────────────────────────────────────────
function Btn({ children, onClick, variant = 'primary', style = {}, disabled = false, small }) {
  const b = {
    display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: T.ff,
    padding: small ? '6px 12px' : '10px 18px', fontSize: small ? 11 : 13,
    fontWeight: 600, borderRadius: 8, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1, transition: 'all 0.2s', letterSpacing: '0.03em', ...style,
  };
  const vs = {
    primary: { background: T.gold, color: '#000' },
    outline: { background: 'transparent', color: T.gold, border: `1px solid ${T.gold}55` },
    ghost:   { background: 'transparent', color: T.muted, border: `1px solid ${T.border}` },
    cyan:    { background: T.cyan, color: '#000' },
    danger:  { background: T.red, color: '#fff' },
    green:   { background: T.green, color: '#000' },
  };
  return <button onClick={disabled ? undefined : onClick} style={{ ...b, ...vs[variant] }}>{children}</button>;
}

function Card({ children, style = {} }) {
  return <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, ...style }}>{children}</div>;
}

function Badge({ text, color }) {
  const c = color || stageColor(text);
  return <span style={{ fontSize: 10, padding: '3px 9px', borderRadius: 4, background: c + '22', color: c, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase' }}>{text?.replace(/_/g, ' ')}</span>;
}

function Input({ label, value, onChange, placeholder, type = 'text', required }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>{label}{required && <span style={{ color: T.red }}> *</span>}</div>
      <input value={value || ''} onChange={e => onChange(e.target.value)} type={type} placeholder={placeholder}
        style={{ width: '100%', padding: '9px 12px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 7, color: T.text, fontSize: 13, fontFamily: T.ff, outline: 'none' }} />
    </div>
  );
}

function Textarea({ label, value, onChange, rows = 3, placeholder }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>{label}</div>
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder}
        style={{ width: '100%', padding: '9px 12px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 7, color: T.text, fontSize: 13, fontFamily: T.ff, outline: 'none', resize: 'vertical' }} />
    </div>
  );
}

// ── LOGIN ─────────────────────────────────────────────────────
function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [pass, setPass]   = useState('');
  const [err, setErr]     = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true); setErr('');
    try {
      const { token, admin } = await adminLogin(email, pass);
      localStorage.setItem('walkin_token', token);
      localStorage.setItem('walkin_admin', JSON.stringify(admin));
      onLogin(admin);
    } catch (e) {
      setErr(e.response?.data?.error || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: T.ff }}>
      <div style={{ width: 380, padding: 36, background: T.surface, borderRadius: 16, border: `1px solid ${T.border}` }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🏙️</div>
          <div style={{ fontFamily: T.ffD, fontSize: 26, color: T.text }}>Roovivo Admin</div>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>Simon Mamo Real Estate Portal</div>
        </div>
        <Input label="Email" value={email} onChange={setEmail} type="email" placeholder="carlos@simonmamo.com" />
        <Input label="Password" value={pass} onChange={setPass} type="password" placeholder="••••••••••" />
        {err && <div style={{ color: T.red, fontSize: 12, marginBottom: 12 }}>⚠️ {err}</div>}
        <Btn onClick={submit} disabled={loading || !email || !pass} style={{ width: '100%', padding: '12px', fontSize: 14, justifyContent: 'center' }}>
          {loading ? 'Signing in…' : 'Sign In →'}
        </Btn>
        <p style={{ textAlign: 'center', marginTop: 14, fontSize: 10, color: T.dim }}>
          First time? Visit <code style={{ color: T.cyan }}>/api/admin/setup</code> to create your account.
        </p>
      </div>
    </div>
  );
}

// ── DASHBOARD ─────────────────────────────────────────────────
function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { adminStats().then(setStats).catch(console.error); }, []);
  if (!stats) return <div style={{ padding: 40, color: T.muted }}>Loading stats…</div>;

  const cards = [
    { icon: '🏛️', label: 'Total Listings',   val: stats.properties.total,     sub: `${stats.properties.available} available`, color: T.cyan   },
    { icon: '📋', label: 'Active Deals',      val: stats.reservations.active,  sub: `${stats.reservations.total} total`,        color: T.gold   },
    { icon: '🎉', label: 'Completed Sales',   val: stats.reservations.completed, sub: 'This year',                              color: T.green  },
    { icon: '💶', label: 'Reservation Fees',  val: fmt(stats.revenue),          sub: 'Collected via Stripe',                    color: T.purple },
  ];

  return (
    <div>
      <h2 style={{ fontFamily: T.ffD, fontSize: 28, color: T.text, marginBottom: 22 }}>Dashboard</h2>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 28 }}>
        {cards.map(c => (
          <Card key={c.label} style={{ padding: '18px 20px' }}>
            <div style={{ fontSize: 26, marginBottom: 8 }}>{c.icon}</div>
            <div style={{ fontFamily: T.ffD, fontSize: 30, color: c.color, fontWeight: 700 }}>{c.val}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginTop: 2 }}>{c.label}</div>
            <div style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>{c.sub}</div>
          </Card>
        ))}
      </div>

      {/* Prop breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 14 }}>Property Status</div>
          {[['🟢 Available', stats.properties.available, T.cyan], ['🟡 Reserved', stats.properties.reserved, T.gold], ['⚫ Sold', stats.properties.sold, T.muted]].map(([l,v,c]) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${T.border}`, fontSize: 13 }}>
              <span style={{ color: T.muted }}>{l}</span><span style={{ color: c, fontWeight: 700 }}>{v}</span>
            </div>
          ))}
        </Card>
        <Card style={{ padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 14 }}>Recent Reservations</div>
          {(stats.recentReservations || []).map(r => (
            <div key={r._id} style={{ padding: '7px 0', borderBottom: `1px solid ${T.border}`, fontSize: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: T.text, fontWeight: 500 }}>{r.name}</span>
                <Badge text={r.stage} />
              </div>
              <div style={{ color: T.muted, marginTop: 2 }}>{r.propertyTitle}</div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ── PROPERTIES MANAGER ────────────────────────────────────────
function PropertiesManager() {
  const [props, setProps]     = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState({});
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]         = useState('');

  const load = () => getProperties().then(setProps).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const openNew  = () => { setForm({ feats: '', rooms: '', status: 'available', tag: 'New Listing', col: 1, row: 1 }); setEditing('new'); };
  const openEdit = p  => { setForm({ ...p, feats: (p.feats || []).join(', '), rooms: (p.rooms || []).join(', ') }); setEditing(p._id); };

  const save = async () => {
    const data = {
      ...form,
      feats: form.feats?.split(',').map(s => s.trim()).filter(Boolean),
      rooms: form.rooms?.split(',').map(s => s.trim()).filter(Boolean),
      price: Number(form.price), beds: Number(form.beds), baths: Number(form.baths),
      sqm: Number(form.sqm), floor: Number(form.floor), col: Number(form.col), row: Number(form.row),
    };
    const payload = new FormData();
    payload.append('data', JSON.stringify(data));
    try {
      if (editing === 'new') await createProperty(payload);
      else await updateProperty(editing, payload);
      setMsg('✅ Saved!'); setEditing(null); load();
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { setMsg('❌ ' + (e.response?.data?.error || e.message)); }
  };

  const del = async id => {
    if (!window.confirm('Delete this property?')) return;
    await deleteProperty(id); load();
  };

  const seed = async () => {
    setLoading(true);
    try { await seedProperties(); setMsg('✅ Valletta seed data loaded!'); load(); setTimeout(() => setMsg(''), 4000); }
    catch (e) { setMsg('❌ ' + e.message); setLoading(false); }
  };

  const statusColor = s => ({ available: T.cyan, reserved: T.gold, sold: T.green }[s] || T.muted);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontFamily: T.ffD, fontSize: 28, color: T.text }}>Listings Manager</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {msg && <span style={{ fontSize: 12, color: T.cyan, alignSelf: 'center' }}>{msg}</span>}
          <Btn onClick={seed} variant="ghost">🌱 Seed Valletta</Btn>
          <Btn onClick={openNew}>+ New Listing</Btn>
        </div>
      </div>

      {editing && (
        <Card style={{ padding: 22, marginBottom: 20 }}>
          <div style={{ fontFamily: T.ffD, fontSize: 20, color: T.gold, marginBottom: 16 }}>
            {editing === 'new' ? '+ New Property' : 'Edit Property'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0 }}>
            <div style={{ paddingRight: 12 }}>
              <Input label="Title"    value={form.title}  onChange={v => set('title', v)}  required />
              <Input label="Address"  value={form.addr}   onChange={v => set('addr', v)}   required />
              <Input label="Price (€)"value={form.price}  onChange={v => set('price', v)}  type="number" required />
            </div>
            <div style={{ paddingRight: 12 }}>
              <Input label="Beds"     value={form.beds}   onChange={v => set('beds', v)}   type="number" />
              <Input label="Baths"    value={form.baths}  onChange={v => set('baths', v)}  type="number" />
              <Input label="Sqm"      value={form.sqm}    onChange={v => set('sqm', v)}    type="number" />
            </div>
            <div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Status</div>
                <select value={form.status || 'available'} onChange={e => set('status', e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 7, color: T.text, fontSize: 13, fontFamily: T.ff }}>
                  <option value="available">Available</option>
                  <option value="reserved">Reserved</option>
                  <option value="sold">Sold</option>
                </select>
              </div>
              <Input label="Tag (e.g. 🔥 Hot)" value={form.tag} onChange={v => set('tag', v)} />
              <div style={{ display: 'flex', gap: 8 }}>
                <Input label="City Col" value={form.col} onChange={v => set('col', v)} type="number" />
                <Input label="City Row" value={form.row} onChange={v => set('row', v)} type="number" />
              </div>
            </div>
          </div>
          <Textarea label="Description" value={form.desc} onChange={v => set('desc', v)} rows={3} placeholder="Property description…" />
          <Input label="Features (comma-separated)" value={form.feats} onChange={v => set('feats', v)} placeholder="Harbour View, Terrace, Smart Home, …" />
          <Input label="Rooms (comma-separated with emoji)" value={form.rooms} onChange={v => set('rooms', v)} placeholder="🏛️ Grand Salon, 🛏 Master Suite, …" />
          <Input label="Matterport Tour ID" value={form.matterportId} onChange={v => set('matterportId', v)} placeholder="Optional — paste Matterport model ID" />
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <Btn onClick={save}>💾 Save Listing</Btn>
            <Btn onClick={() => setEditing(null)} variant="ghost">Cancel</Btn>
          </div>
        </Card>
      )}

      {loading ? (
        <div style={{ color: T.muted, padding: 24 }}>Loading listings…</div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {props.map(p => (
            <Card key={p._id} style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                  <span style={{ fontWeight: 600, fontSize: 14, color: T.text }}>{p.title}</span>
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: statusColor(p.status) + '22', color: statusColor(p.status) }}>{p.status}</span>
                  <span style={{ fontSize: 10, color: T.dim }}>{p.tag}</span>
                </div>
                <div style={{ fontSize: 11, color: T.muted }}>{p.addr} · {p.beds}bd · {p.sqm}m² · Col {p.col}, Row {p.row}</div>
              </div>
              <div style={{ fontFamily: T.ffD, fontSize: 20, color: T.gold, flexShrink: 0 }}>{fmt(p.price)}</div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <Btn small onClick={() => openEdit(p)} variant="outline">Edit</Btn>
                <Btn small onClick={() => del(p._id)} variant="danger">Del</Btn>
              </div>
            </Card>
          ))}
          {!props.length && (
            <div style={{ padding: 40, textAlign: 'center', color: T.muted }}>
              No listings yet. Click <strong style={{ color: T.gold }}>Seed Valletta</strong> to load sample data, or add a listing manually.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── RESERVATIONS / DEAL CRM ───────────────────────────────────
function ReservationsPanel() {
  const [list, setList]       = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail]   = useState(null);
  const [note, setNote]       = useState('');
  const [stage, setStage]     = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]         = useState('');

  const load = () => getReservations().then(r => { setList(r); setLoading(false); }).catch(console.error);
  useEffect(() => { load(); }, []);

  const openDetail = async id => {
    setSelected(id);
    const r = await getReservation(id).catch(console.error);
    setDetail(r);
    setStage(r?.stage || '');
  };

  const saveStage = async () => {
    if (!stage) return;
    await advanceStage(selected, stage, note);
    setMsg('✅ Stage updated & buyer notified');
    setNote('');
    await openDetail(selected);
    await load();
    setTimeout(() => setMsg(''), 4000);
  };

  const saveNote = async () => {
    if (!note.trim()) return;
    await addNote(selected, note);
    setNote('');
    await openDetail(selected);
  };

  const verify = async docType => {
    await verifyDoc(selected, docType);
    await openDetail(selected);
  };

  const DocRow = ({ label, key2, doc }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${T.border}`, fontSize: 12 }}>
      <span style={{ color: T.muted }}>{label}</span>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {doc?.uploaded
          ? <span style={{ color: T.cyan }}>✓ Uploaded</span>
          : <span style={{ color: T.dim }}>Pending</span>}
        {doc?.verified
          ? <span style={{ color: T.green, fontSize: 10 }}>✓ Verified</span>
          : doc?.uploaded
            ? <Btn small variant="green" onClick={() => verify(key2)}>Verify</Btn>
            : null}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 140px)', gap: 12, overflow: 'hidden' }}>
      {/* List */}
      <div style={{ width: 300, flexShrink: 0, overflowY: 'auto' }}>
        <h2 style={{ fontFamily: T.ffD, fontSize: 24, color: T.text, marginBottom: 14 }}>Deals ({list.length})</h2>
        {loading ? <div style={{ color: T.muted }}>Loading…</div> : (
          list.map(r => (
            <Card key={r._id} style={{ padding: '13px 15px', marginBottom: 7, cursor: 'pointer',
              borderColor: selected === r._id ? T.gold + '66' : T.border,
              background: selected === r._id ? T.card2 : T.card }}
              onClick={() => openDetail(r._id)}>
              <div style={{ fontWeight: 600, fontSize: 13, color: T.text, marginBottom: 3 }}>{r.name}</div>
              <div style={{ fontSize: 11, color: T.muted, marginBottom: 6 }}>{r.propertyTitle}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Badge text={r.stage} />
                <span style={{ fontSize: 10, color: T.dim }}>{dateStr(r.createdAt)}</span>
              </div>
            </Card>
          ))
        )}
        {!loading && !list.length && <div style={{ color: T.muted, padding: 16, fontSize: 13 }}>No reservations yet.</div>}
      </div>

      {/* Detail */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {!detail ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: T.dim, fontSize: 14 }}>
            Select a deal to view details
          </div>
        ) : (
          <div>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
              <div>
                <div style={{ fontFamily: T.ffD, fontSize: 24, color: T.text }}>{detail.name}</div>
                <div style={{ fontSize: 12, color: T.muted }}>{detail.email} · {detail.phone} · {detail.country}</div>
                {detail.isNonEU && <div style={{ marginTop: 5, fontSize: 11, color: T.gold }}>⚠️ AIP Permit Required — Status: {detail.aipStatus}</div>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: T.ffD, fontSize: 22, color: T.gold }}>{fmt(detail.propertyPrice)}</div>
                <div style={{ fontSize: 11, color: T.muted }}>Ref: WKN-{detail._id?.slice(-6).toUpperCase()}</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              {/* Stage control */}
              <Card style={{ padding: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 10 }}>⚡ Advance Stage</div>
                <select value={stage} onChange={e => setStage(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 7, color: T.text, fontSize: 12, fontFamily: T.ff, marginBottom: 8 }}>
                  {STAGE_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <Textarea label="Internal Note (optional)" value={note} onChange={setNote} rows={2} placeholder="Note for this stage update…" />
                {msg && <div style={{ fontSize: 11, color: T.cyan, marginBottom: 8 }}>{msg}</div>}
                <Btn onClick={saveStage} variant="cyan" style={{ width: '100%', justifyContent: 'center' }}>
                  Update & Notify Buyer
                </Btn>
              </Card>

              {/* Documents */}
              <Card style={{ padding: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 10 }}>📎 KYC Documents</div>
                <DocRow label="🪪 Passport"         key2="passport"     doc={detail.docs?.passport} />
                <DocRow label="💰 Proof of Funds"   key2="proofOfFunds" doc={detail.docs?.proofOfFunds} />
                <DocRow label="📄 Source of Funds"  key2="sourceFunds"  doc={detail.docs?.sourceFunds} />
                <DocRow label="✍️ PoA Signed"        key2="poaSigned"    doc={detail.docs?.poaSigned} />
              </Card>
            </div>

            {/* Notes timeline */}
            <Card style={{ padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 12 }}>📝 Internal Notes</div>
              {(detail.notes || []).map((n, i) => (
                <div key={i} style={{ padding: '8px 10px', background: T.surface, borderRadius: 7, marginBottom: 7, fontSize: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span style={{ color: T.cyan, fontWeight: 600 }}>{n.author}</span>
                    <span style={{ color: T.dim }}>{dateStr(n.at)}</span>
                  </div>
                  <div style={{ color: T.muted }}>{n.text}</div>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input value={note} onChange={e => setNote(e.target.value)} placeholder="Add a note…"
                  style={{ flex: 1, padding: '8px 11px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 7, color: T.text, fontSize: 12, fontFamily: T.ff, outline: 'none' }}
                  onKeyDown={e => e.key === 'Enter' && saveNote()} />
                <Btn small onClick={saveNote} variant="outline">Add</Btn>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

// ── MAIN ADMIN APP ────────────────────────────────────────────
export default function AdminPanel() {
  const [admin, setAdmin] = useState(() => {
    try { return JSON.parse(localStorage.getItem('walkin_admin')); } catch { return null; }
  });
  const [page, setPage] = useState('dashboard');

  const logout = () => {
    localStorage.removeItem('walkin_token');
    localStorage.removeItem('walkin_admin');
    setAdmin(null);
  };

  if (!admin) return <Login onLogin={a => setAdmin(a)} />;

  const NAV = [
    { id: 'dashboard',  icon: '📊', label: 'Dashboard'  },
    { id: 'properties', icon: '🏛️', label: 'Listings'   },
    { id: 'deals',      icon: '📋', label: 'Deals / CRM'},
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: T.bg, fontFamily: T.ff }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=Outfit:wght@300;400;500;600;700&display=swap'); * { box-sizing: border-box; margin: 0; padding: 0; } ::-webkit-scrollbar { width: 4px } ::-webkit-scrollbar-thumb { background: #C9A96E44 } input,select,textarea { outline: none }`}</style>

      {/* Sidebar */}
      <div style={{ width: 220, background: T.surface, borderRight: `1px solid ${T.border}`, padding: '20px 14px', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ padding: '0 6px 20px', borderBottom: `1px solid ${T.border}`, marginBottom: 16 }}>
          <div style={{ fontFamily: T.ffD, fontSize: 22, color: T.text }}>🏙️ Roovivo</div>
          <div style={{ fontSize: 10, color: T.muted, marginTop: 2, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Admin Portal</div>
        </div>
        {NAV.map(n => (
          <button key={n.id} onClick={() => setPage(n.id)}
            style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 10px', marginBottom: 3, borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: T.ff, fontSize: 13, fontWeight: page === n.id ? 700 : 400, background: page === n.id ? T.goldGlow : 'transparent', color: page === n.id ? T.gold : T.muted, transition: 'all 0.2s', textAlign: 'left' }}>
            {n.icon} {n.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <div style={{ padding: '14px 6px 0', borderTop: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 11, color: T.muted, marginBottom: 8 }}>👤 {admin.name || admin.email}</div>
          <a href="https://roovivo.com" target="_blank" rel="noreferrer" style={{ display: 'block', fontSize: 11, color: T.cyan, textDecoration: 'none', marginBottom: 6 }}>🔗 View Site</a>
          <button onClick={logout} style={{ fontSize: 11, color: T.red, background: 'none', border: 'none', cursor: 'pointer', fontFamily: T.ff, padding: 0 }}>Sign Out</button>
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, padding: 28, overflowY: 'auto' }}>
        {page === 'dashboard'  && <Dashboard />}
        {page === 'properties' && <PropertiesManager />}
        {page === 'deals'      && <ReservationsPanel />}
      </div>
    </div>
  );
}
